// Alhaalta nouseva arkki. Sulkeutuu taustaa napauttamalla tai Esc-näppäimellä,
// ja kohdistus siirtyy arkkiin avautuessa (näppäimistö ja ruudunlukija).
// Androidin takaisin-painike sulkee päällimmäisen arkin (lib/backStack.js).

import { createPortal } from "preact/compat";
import { useEffect, useRef } from "preact/hooks";

import { backStack } from "../lib/backStack.js";
import { nextFocusIndex, sheetStack } from "../lib/sheetStack.js";

let nextSheetId = 0;
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Päällimmäinen ikkuna on ainoa vuorovaikutteinen: kaikki muut sivun juuren lapset (sovellus, alemmat ikkunat) ovat `inert`. */
function syncInert() {
  const scrims = [...document.body.children].filter((el) => el.classList?.contains("scrim"));
  const top = scrims[scrims.length - 1] ?? null;
  for (const el of document.body.children) {
    if (el.tagName === "SCRIPT") continue;
    if (top && el !== top) el.setAttribute("inert", "");
    else el.removeAttribute("inert");
  }
}

export function Sheet({ title, onClose, children, labelledBy = "sheet-title" }) {
  const ref = useRef(null);
  // Takaisin-painike kutsuu aina tuoreinta onClose-funktiota, vaikka se on vaihtunut avautumisen jälkeen.
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    const previous = document.activeElement;
    const id = ++nextSheetId;
    const unstack = sheetStack.push(id);
    syncInert();
    ref.current?.focus();
    const onKey = (e) => {
      if (!sheetStack.isTop(id)) return; // alempi ikkuna ei reagoi, kun päällä on toinen
      if (e.key === "Escape") return closeRef.current();
      if (e.key !== "Tab" || !ref.current) return;
      // Kohdistus kiertää ikkunan sisällä, vaikka selain ei tukisi `inert`-määrettä.
      const items = [...ref.current.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null || el === document.activeElement);
      const next = nextFocusIndex(items.length, items.indexOf(document.activeElement), e.shiftKey);
      if (next >= 0) {
        e.preventDefault();
        items[next].focus();
      }
    };
    window.addEventListener("keydown", onKey);
    const unregister = backStack.push(() => closeRef.current());
    return () => {
      unregister();
      unstack();
      window.removeEventListener("keydown", onKey);
      // Portaalin elementti poistuu vasta tämän jälkeen, joten päivitys tehdään seuraavassa tickissä.
      setTimeout(() => {
        syncInert();
        previous?.focus?.(); // vasta kun tausta on taas käytössä (`inert` estäisi kohdistuksen)
      }, 0);
    };
  }, []);

  // Portaali sivun juureen: muuten kartan päällä avattu arkki jää kartan pinoamiskontekstiin ja
  // "Aloita retki" -painike sekä alapalkki peittävät sen napit.
  return createPortal(
    <div class="scrim" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div class="sheet" role="dialog" aria-modal="true" aria-labelledby={labelledBy} tabIndex={-1} ref={ref}>
        <div class="grabber" aria-hidden="true" />
        {title && <h2 id={labelledBy}>{title}</h2>}
        {children}
      </div>
    </div>,
    document.body,
  );
}
